import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { QuizDetailsFormComponent } from "../../components/quiz-details-form/quiz-details-form.component";
import { QuiztimeApiService } from '../../../../services/quiztime-api.service';
import { ImageUploadService } from '../../../../services/image-upload.service';
import { catchError, concatMap, finalize, Observable, of, tap, forkJoin } from 'rxjs';
import { AuthStore } from '../../../../store/auth.store';
import { IFormDirtyGuard } from '../../../../guards/form-dirty.guard';
import { Role } from '../../../../models/role.enum';
import { EnrollmentState } from '../../../../models/enrollment-state.enum';
import { QuizAdministratorView } from '../../../../models/views/quiz-administrator-view.interface';
import { EnrollmentsRequest } from '../../../../models/enrollments-request.interface';
import { EnrollmentRequest } from '../../../../models/enrollment-request.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { Quiz } from '../../../../models/quiz.interface';
import { Enrollment } from '../../../../models/enrollment.interface';
import { ToastService } from '../../../../services/toast.service';
import { QuizStateService } from '../../../../services/quiz-state.service';
import { QuizDetailsFormLoaderComponent } from "../../components/quiz-details-form/quiz-details-form-loader.component";

@Component({
    selector: 'app-quiz-details-form-container',
    imports: [QuizDetailsFormComponent, QuizDetailsFormLoaderComponent],
    template: `
    @if(loadingQuiz && !error) {
        <app-quiz-details-form-loader></app-quiz-details-form-loader>
      }
      @else{
        <app-quiz-details-form
          #quizDetailsForm
          (formSubmitted)="handleSubmit($event)"
          (adminstratorsAdded)="addAdministrators($event)"
          (administratorRemoved)="removeAdministrator($event)"
          [administrators]="initialAdmins"
          [pendingAdminEnrollments]="pendingAdminEnrollments"
          [owner]="owner"
          [error]="error"
          [bannerError]="bannerError"
          [isSubmitting]="isSubmitting"
          [isSubmittingAdmins]="isSubmittingAdmins"
          [quiz]="quiz"
          [isEditMode]="isEditMode"
          [canEdit]="canEdit"
          [loadingQuiz]="loadingQuiz"
          [user]="user!"
          [deletingAdminEmail]="deletingAdminEmail()"
        ></app-quiz-details-form>
      }
  `
})
export class QuizDetailsFormContainerComponent implements OnInit, IFormDirtyGuard {
  private quiztimeApi: QuiztimeApiService = inject(QuiztimeApiService);
  private imageUploadService = inject(ImageUploadService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private quizStateService = inject(QuizStateService);
  authStore: AuthStore = inject(AuthStore);
  owner: QuizAdministratorView = {} as QuizAdministratorView;
  initialAdmins: QuizAdministratorView[] = [];
  pendingAdminEnrollments: QuizAdministratorView[] = [];
  user = this.authStore.user();
  error!: string;
  bannerError!: string;
  isSubmitting: boolean = false;
  isSubmittingAdmins: boolean = false;
  isEditMode: boolean = false;
  canEdit: boolean = true;
  quiz: Quiz | null = null;
  loadingQuiz: boolean = true;
  deletingAdminEmail = signal<string | null>(null);

  @ViewChild('quizDetailsForm') quizDetailsFormComponent!: QuizDetailsFormComponent;

  ngOnInit() {
    const user = this.authStore.user();
    if (!user) {
      throw new Error('User is not authenticated');
    }

    this.route.paramMap.subscribe(params => {
      const quizGuid = params.get('quizGuid');
      if (quizGuid) {
        this.isEditMode = true;
        this.loadQuizData(quizGuid);
      }
      else {
        this.loadingQuiz = false;
        this.owner = {
          email: user.ltiUser.email,
          firstName: user.ltiUserProfile?.first_name ?? '',
          lastName: user.ltiUserProfile?.last_name ?? '',
          role: Role.Owner,
          status: EnrollmentState.Active
        };
        this.initialAdmins = [this.owner];
      }
    });
  }

  canDeactivate = () => !this.quizDetailsFormComponent.quizDetailsForm.dirty;

  saveConfirmed = () => this.quizDetailsFormComponent.handleSubmit();

  handleSubmit(event: QuizRequestForm) {
    this.isSubmitting = true;
    this.error = '';
    this.bannerError = '';

    let action;
    if (this.isEditMode) {
      action = this.updateQuizAction(event);
    }
    else {
      action = this.createQuizAction(event);
    }

    action
      .pipe(
        finalize(() => this.isSubmitting = false)
      )
      .subscribe(() => {
        // Update the quiz name in the shared state, to be displayed in quiz wrapper
        this.quizStateService.setQuizName(event.form.name);
        this.quizDetailsFormComponent.quizDetailsForm.markAsPristine();
        this.router.navigate(['..', event.form.quiz_guid], { relativeTo: this.route });
        this.toastService.success('Quiz saved successfully');
      });
  }

  addAdministrators(event: string[]) {
    const emails = event;
    const newEnrollments = emails.map(email => ({
      email,
      role: Role.Admin,
      status: EnrollmentState.Unknown,
      isWorkingAdding: true
    }));
    const allEnrollments = [...this.pendingAdminEnrollments, ...newEnrollments];
    this.pendingAdminEnrollments = allEnrollments.filter((enrollment, index, self) =>
      index === self.findIndex(e => e.email === enrollment.email)
    );

    // Process each email separately to determine if they are existing users
    emails.forEach(email => {
      this.quiztimeApi.getUserByEmail(email).subscribe({
        next: (user) => {
          if (this.isEditMode) {
            // Existing quiz - process immediately
            if (user) {
              // User exists - add as enrollment
              const enrollmentRequest: EnrollmentsRequest = {
                enrollments: [{
                  email: email,
                  role: Role.Admin,
                }]
              };

              this.quiztimeApi.addEnrollments(this.quizDetailsFormComponent.quizGuid, enrollmentRequest).pipe(
                tap(() => {
                  this.toastService.success(`Added administrator: ${email}`);
                }),
                concatMap(() => this.refreshAdmins(this.quizDetailsFormComponent.quizGuid)),
                catchError(err => {
                  this.toastService.error(`Failed to add administrator: ${email}`);
                  return of(null);
                }),
                finalize(() => {
                  this.updatePendingAdminEnrollment(email, false);
                  this.removeEmailFromPendingAdminEnrollments(email);
                })
              ).subscribe();
            } else {
              // User doesn't exist - send invitation
              this.quiztimeApi.inviteLearners(this.quizDetailsFormComponent.quizGuid, [email], Role.Admin).pipe(
                tap(() => {
                  this.toastService.success(`Invited administrator: ${email}`);
                }),
                concatMap(() => this.refreshAdmins(this.quizDetailsFormComponent.quizGuid)),
                catchError(err => {
                  this.toastService.error(`Failed to invite administrator: ${email}`);
                  return of(null);
                }),
                finalize(() => {
                  this.updatePendingAdminEnrollment(email, false);
                  this.removeEmailFromPendingAdminEnrollments(email);
                })
              ).subscribe();
            }
          } else {
            // New quiz - just mark the enrollment as processed since we'll handle it on save
            this.updatePendingAdminEnrollment(email, false, user !== null);
          }
        },
        error: (err) => {
          this.toastService.error(`Error checking user status for: ${email}`);
          this.updatePendingAdminEnrollment(email, false);
          this.removeEmailFromPendingAdminEnrollments(email);
        }
      });
    });
  }

  private removeEmailFromPendingAdminEnrollments(email: string) {
    this.pendingAdminEnrollments = this.pendingAdminEnrollments.filter(admin => admin.email !== email);
  }

  private updatePendingAdminEnrollment(email: string, isWorking: boolean, isExistingUser?: boolean) {
    this.pendingAdminEnrollments = this.pendingAdminEnrollments.map(admin => {
      if (admin.email === email) {
        const updated = { ...admin, isWorkingAdding: isWorking };
        if (isExistingUser !== undefined) {
          // @ts-ignore - Adding custom field to track if user exists
          updated.isExistingUser = isExistingUser;
        }
        return updated;
      }
      return admin;
    });
  }

  removeAdministrator(email: string) {
    if (this.owner.email === email) {
      throw new Error('Cannot remove owner');
    }
    this.deletingAdminEmail.set(email);
    this.pendingAdminEnrollments = [...this.pendingAdminEnrollments.filter(admin => admin.email !== email)];
    this.quiztimeApi.deleteEnrollment(this.quizDetailsFormComponent.quizGuid, email, Role.Admin)
      .pipe(
        tap(() => {
          this.toastService.success(`Removed administrator: ${email}`);
        }),
        concatMap(() => this.refreshAdmins(this.quizDetailsFormComponent.quizGuid)),
        catchError(err => {
          this.toastService.error(`Failed to remove administrator: ${email}`);
          return of(null);
        }),
        finalize(() => {
          this.deletingAdminEmail.set(null);
        })
      )
      .subscribe();
  }

  private refreshAdmins(quizGuid: string) : Observable<Enrollment[] | null> {
    return this.quiztimeApi.getEnrollments(quizGuid, [EnrollmentState.Active, EnrollmentState.Invited], [Role.Admin])
      .pipe(
        tap((admins: Enrollment[]) => {
          this.initialAdmins = admins.map(admin => ({
            email: admin.user.email,
            firstName: admin.profile?.first_name ?? '',
            lastName: admin.profile?.last_name ?? '',
            role: admin.role,
            status: admin.state
          }));
        }),
        catchError(err => {
          this.error = 'We encountered an error while loading the administrators. Please try again.';
          return of(null)
        })
      );
  }

  private loadQuizData(quizGuid: string) {
    this.quiztimeApi.getQuiz(quizGuid)
      .pipe(
        tap(quiz => {
          this.quiz = quiz;
          // Update the quiz name in the shared state when the quiz is loaded
          if (quiz && quiz.name) {
            this.quizStateService.setQuizName(quiz.name);
          }
        }),
        concatMap(() => this.refreshAdmins(quizGuid)),
        catchError(err => {
          this.error = 'We encountered an error while loading the quiz. Please try again.';
          this.canEdit = false;
          return of(null)
        }),
        finalize(() => this.loadingQuiz = false)
      ).subscribe();
  }

  private handleBannerUploadError(err: any): void {
    if (err?.message?.includes('Invalid image format')) {
      this.bannerError = err.message;
    } else {
      this.bannerError = 'Failed to upload banner image. Please try again.';
    }
  }

  private updateQuizAction(event: QuizRequestForm) : Observable<any> {
    return this.quiztimeApi.updateQuiz(event.form)
      .pipe(
        concatMap(() => {
          if (!event.image.dirty) return of(null);
          if (!event.image.localFile) return of(null);
          return this.imageUploadService.uploadImage(event.form.quiz_guid, event.image.localFile, true)
            .pipe(
              catchError(err => {
                this.handleBannerUploadError(err);
                throw err;
              })
            )
        }),
        tap((resp) => console.log('Media updated', resp)),
        catchError(err => {
          if (!this.bannerError && !this.error) {
            this.error = 'We encountered an error while updating the quiz. Please try again.';
          }
          throw err;
        })
      )
  }

  private createQuizAction(event: QuizRequestForm) : Observable<any> {
    return this.quiztimeApi.createQuiz(event.form)
      .pipe(
        concatMap((createdGuid: string) => {
          if (event.image.localFile === null) return of(createdGuid);
          return this.imageUploadService.uploadImage(createdGuid, event.image.localFile, true)
            .pipe(
              catchError(err => {
                this.handleBannerUploadError(err);
                throw err;
              })
            )
        }),
        concatMap(() => {
          // Separate existing users from non-existing users
          const existingUserEmails: string[] = [];
          const nonExistingUserEmails: string[] = [];

          this.pendingAdminEnrollments.forEach(admin => {
            // Skip the owner as we'll handle them separately
            if (admin.email === this.owner.email) return;

            // @ts-ignore - Using our custom isExistingUser field
            if (admin.isExistingUser) {
              existingUserEmails.push(admin.email);
            } else {
              nonExistingUserEmails.push(admin.email);
            }
          });

          const operations: Observable<any>[] = [];

          // Add the owner and any existing admin users in a single call
          const allEnrollments = [
            this.owner,
            ...existingUserEmails.map(email => ({
              email,
              role: Role.Admin,
              status: EnrollmentState.Active
            }))
          ];

          operations.push(
            this.quiztimeApi.addEnrollments(event.form.quiz_guid, { enrollments: allEnrollments })
              .pipe(
                catchError(err => {
                  this.toastService.error('Failed to add some administrators');
                  return of(null);
                })
              )
          );

          // If we have non-existing users, invite them
          if (nonExistingUserEmails.length > 0) {
            operations.push(
              this.quiztimeApi.inviteLearners(event.form.quiz_guid, nonExistingUserEmails, Role.Admin)
                .pipe(
                  catchError(err => {
                    this.toastService.error('Failed to invite some administrators');
                    return of(null);
                  })
                )
            );
          }

          // If there are no operations, just return success
          if (operations.length === 0) {
            return of({ success: true });
          }

          // Execute all operations concurrently
          return forkJoin(operations);
        }),
        catchError(err => {
          if (!this.error && !this.bannerError) {
            this.error = 'We encountered an error while creating the quiz. Please try again.';
          }
          throw err;
        }),
      );
  }
}

interface QuizRequestForm {
  form: any
  image: { localFile: File, dirty: boolean }
}
